import { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Trash2, Plus, X, Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { Lender, Account, Contact, LenderProduct } from "@shared/schema";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
// RadioGroup/RadioGroupItem no longer needed in this file — F015 moved to CreatePartiesSection
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { Facility } from "@shared/schema";
import { ProductFormModal } from "@/components/lenders/product-form-modal";
import { CurrencyInput } from "@/components/ui/currency-input"; // F011: Currency formatting for requested amount
// F015: Embedded parties table for Create modal
import { CreatePartiesSection, type CreatePartyEntry } from "@/components/facilities/create-parties-section";
// F016: Pricing schedule section
import { PricingScheduleSection, type PricingScheduleFormData, createDefaultSchedules } from "@/components/facilities/pricing-schedule-section";
import {
  buildFacilityCreateParties,
  buildFacilityCreatePricingSchedules,
} from "@/components/facilities/facility-create-payload";
// F017: Term & Repayment computed display + Fees table
import { TermRepaymentSection } from "@/components/facilities/term-repayment-section";
import { FeesSection, type FeeFormData } from "@/components/facilities/fees-section";

// Facility type options (aligned with lender product categories)
const FACILITY_TYPES = [
  { value: "term_loan", label: "Term Loan" },
  { value: "line_of_credit", label: "Line of Credit" },
  { value: "overdraft", label: "Overdraft" },
  { value: "equipment_finance", label: "Equipment Finance" },
  { value: "invoice_finance", label: "Invoice Finance" },
  { value: "trade_finance", label: "Trade Finance" },
  { value: "commercial_property", label: "Commercial Property" },
  { value: "debtor_finance", label: "Debtor Finance" },
  { value: "other", label: "Other" },
] as const;

// ISSUE-128: Facility party roles
const FACILITY_PARTY_ROLES = [
  { value: "borrower", label: "Borrower" },
  { value: "co_borrower", label: "Co-Borrower" },
  { value: "guarantor", label: "Guarantor" },
  { value: "security_provider", label: "Security Provider" },
] as const;

// ISSUE-128: Facility party type
interface FacilityParty {
  id: string;
  facilityId: string;
  partyType: "account" | "contact";
  accountId?: string;
  contactId?: string;
  partyRole: string;
  partyName?: string; // Resolved name from API
}

// F009: Loan Type options (from schema loan_type_enum)
const LOAN_TYPES = [
  { value: "home_loan", label: "Home Loan" },
  { value: "commercial_loan", label: "Commercial Loan" },
  { value: "equipment_finance", label: "Equipment Finance" },
  { value: "business_loan", label: "Business Loan" },
  { value: "property_development", label: "Property Development" },
  { value: "vehicle_finance", label: "Vehicle Finance" },
] as const;

// F009: Repayment Frequency options (from schema payment_frequency_enum)
const REPAYMENT_FREQUENCIES = [
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
] as const;

// F009: Helper functions for percentage conversion (AGENTS.md pattern)
const decimalToPercent = (value: string | null | undefined): string => {
  if (!value || value === "") return "";
  const decimal = parseFloat(value);
  if (isNaN(decimal)) return "";
  return (decimal * 100).toString();
};

const percentToDecimal = (value: string | null | undefined): string => {
  if (!value || value === "") return "";
  const percent = parseFloat(value);
  if (isNaN(percent)) return "";
  return (percent / 100).toFixed(6);
};

const facilityFormSchema = z.object({
  facilityName: z.string().min(1, "Facility name is required"),
  facilityType: z.string().min(1, "Facility type is required"), // ✅ ISSUE-118: Now required
  facilityStatus: z.string().optional(),
  requestedAmount: z.string().optional(),
  approvedLimit: z.string().optional(),
  fundedAmount: z.string().optional(),
  lenderId: z.string().optional(),
  lenderProductId: z.string().optional(), // ✅ ISSUE-155: Added for cascading product selector
  applicationDate: z.string().optional(),
  approvalDate: z.string().optional(),
  settlementDate: z.string().optional(),
  maturityDate: z.string().optional(),
  // F009: Interest & Repayment fields (user enters as percentages 0-100)
  baseRate: z.string().optional(),
  margin: z.string().optional(),
  termMonths: z.string().optional(),
  ioMonths: z.string().optional(),
  amortisationPeriodMonths: z.string().optional(), // F011: Amortisation period for commercial loans
  repaymentFrequency: z.string().optional(),
  repaymentOverride: z.string().optional(),
  balloonAmount: z.string().optional(),
  balloonPct: z.string().optional(),
  customCommissionRate: z.string().optional(),
  loanType: z.string().optional(),
})
// Refine 1: fundedAmount required when settled (EXISTING - ISSUE-119)
.refine((data) => {
  if (data.facilityStatus === "settled" && !data.fundedAmount) {
    return false;
  }
  return true;
}, {
  message: "Settled Amount is required when facility status is 'settled'",
  path: ["fundedAmount"],
})
// Refine 2: lenderId required when submitted/approved/settled (ISSUE-118)
.refine((data) => {
  const requiresLender = ["submitted", "approved", "settled"].includes(data.facilityStatus || "");
  if (requiresLender && !data.lenderId) {
    return false;
  }
  return true;
}, {
  message: "Lender is required when facility status is 'submitted', 'approved', or 'settled'",
  path: ["lenderId"],
})
// Refine 3: approvedLimit required when approved/settled (ISSUE-118)
.refine((data) => {
  const requiresApproved = ["approved", "settled"].includes(data.facilityStatus || "");
  if (requiresApproved && !data.approvedLimit) {
    return false;
  }
  return true;
}, {
  message: "Approved Limit is required when facility status is 'approved' or 'settled'",
  path: ["approvedLimit"],
})
// Refine 4: approvalDate required when approved/settled (ISSUE-118)
.refine((data) => {
  const requiresApprovalDate = ["approved", "settled"].includes(data.facilityStatus || "");
  if (requiresApprovalDate && !data.approvalDate) {
    return false;
  }
  return true;
}, {
  message: "Approval Date is required when facility status is 'approved' or 'settled'",
  path: ["approvalDate"],
})
// Refine 5: settlementDate required when settled (ISSUE-118)
.refine((data) => {
  if (data.facilityStatus === "settled" && !data.settlementDate) {
    return false;
  }
  return true;
}, {
  message: "Settlement Date is required when facility status is 'settled'",
  path: ["settlementDate"],
})
// F009: Refine 6 - baseRate must be between 0 and 100 (user enters percentage)
.refine((data) => {
  if (data.baseRate) {
    const rate = parseFloat(data.baseRate);
    if (!isNaN(rate) && (rate < 0 || rate > 100)) {
      return false;
    }
  }
  return true;
}, {
  message: "Base rate must be between 0 and 100%",
  path: ["baseRate"],
})
// F009: Refine 7 - margin must be between 0 and 100 (user enters percentage)
.refine((data) => {
  if (data.margin) {
    const rate = parseFloat(data.margin);
    if (!isNaN(rate) && (rate < 0 || rate > 100)) {
      return false;
    }
  }
  return true;
}, {
  message: "Rate must be between 0 and 100%",
  path: ["margin"],
})
// F009: Refine 8 - ioMonths must not exceed termMonths
.refine((data) => {
  if (data.ioMonths && data.termMonths) {
    const io = parseInt(data.ioMonths);
    const term = parseInt(data.termMonths);
    if (!isNaN(io) && !isNaN(term) && io > term) {
      return false;
    }
  }
  return true;
}, {
  message: "I/O period cannot exceed term",
  path: ["ioMonths"],
});

type FacilityFormData = z.infer<typeof facilityFormSchema>;

interface FacilityFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  dealId: string;
  clientGroupId?: string;
  facility?: Facility;
  onDelete?: (facilityId: string) => void;
  // F009: Auto-populate borrower from deal (for new facilities)
  defaultBorrowerId?: string;
  defaultBorrowerType?: "account" | "contact";
}

export function FacilityFormModal({
  isOpen,
  onClose,
  dealId,
  clientGroupId,
  facility,
  onDelete,
  defaultBorrowerId,
  defaultBorrowerType = "account",
}: FacilityFormModalProps) {
  const { toast } = useToast();
  const isEdit = !!facility;

  // Party selection state (legacy — used by Edit mode applicant queries)
  const [partyType, setPartyType] = useState<"account" | "contact">(defaultBorrowerType || "account");
  const [_selectedPartyId, setSelectedPartyId] = useState<string>(defaultBorrowerId || "");
  const [partySearchTerm, setPartySearchTerm] = useState("");
  const [_partyError, setPartyError] = useState<string | null>(null);

  // Collapsible state for additional details
  const [isAdditionalDetailsOpen, setIsAdditionalDetailsOpen] = useState(false);

  // F009: Collapsible state for Interest & Repayment section (default open per E2E test expectations)
  const [isInterestRepaymentOpen, setIsInterestRepaymentOpen] = useState(true);

  // Collapsible state for danger zone
  const [isDangerZoneOpen, setIsDangerZoneOpen] = useState(false);

  // Delete confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Settled confirmation dialog state
  const [showSettledConfirm, setShowSettledConfirm] = useState(false);
  const [previousFacilityStatus, setPreviousFacilityStatus] = useState<string | undefined>(undefined);
  const [settledAmountInput, setSettledAmountInput] = useState<string>("");

  // ISSUE-128: Facility parties state (edit mode only)
  const [facilityParties, setFacilityParties] = useState<FacilityParty[]>([]);
  const [applicantToAdd, setApplicantToAdd] = useState<{
    type: "account" | "contact";
    id: string;
    role: string;
  } | null>(null);
  const [_applicantSearchTerm, setApplicantSearchTerm] = useState("");
  // ISSUE-128 Rework #1: Edit state management
  const [editingParty, setEditingParty] = useState<FacilityParty | null>(null);

  // ISSUE-155: Product selector state
  const [showProductModal, setShowProductModal] = useState(false);
  const [previousLenderId, setPreviousLenderId] = useState<string | undefined>(undefined);

  // F015: Parties state for Create mode (replaces single borrower selector)
  const [createParties, setCreateParties] = useState<CreatePartyEntry[]>([]);
  const [partyValidationError, setPartyValidationError] = useState<string | null>(null);

  // F016: Pricing schedules state for Create mode
  const [pricingSchedules, setPricingSchedules] = useState<PricingScheduleFormData[]>(createDefaultSchedules());

  // F017: Fees state for Create mode
  const [createFees, setCreateFees] = useState<FeeFormData[]>([]);

  // F017: Date state for Create mode (finance clause, expected settlement)
  const [financeClauseDate, setFinanceClauseDate] = useState("");
  const [expectedSettlementDate, setExpectedSettlementDate] = useState("");

  // ⚠️ CRITICAL: useForm MUST be declared BEFORE any calls to watch()
  // ISSUE-155 Rework #1: Moved from line 288 to fix "Cannot access 'watch' before initialization"
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    trigger,
  } = useForm<FacilityFormData>({
    resolver: zodResolver(facilityFormSchema),
    mode: 'all', // F009: Enable validation on both change and blur for real-time feedback including cross-field validation
    reValidateMode: 'onChange', // Re-validate on change after first validation
    defaultValues: {
      facilityType: "term_loan", // F009: Default facility type to prevent validation blocking
      facilityStatus: "indicative",
    },
  });

  // Fetch lenders for dropdown
  const { data: lenders = [], isLoading: isLoadingLenders } = useQuery<Lender[]>({
    queryKey: ["/api/crm/lenders", { status: "active" }],
    enabled: isOpen,
  });

  // ISSUE-155: Fetch lender products when lender is selected (uses default queryFn with auth)
  const watchLenderId = watch("lenderId");
  const { data: lenderProducts = [], isLoading: isLoadingProducts } = useQuery<LenderProduct[]>({
    queryKey: ["/api/crm/lenders", watchLenderId, "products", { status: "active" }],
    enabled: isOpen && !!watchLenderId && watchLenderId !== "__none__",
  });

  // ISSUE-128 Rework #1: Fetch accounts for party selector (uses default queryFn with auth)
  const { data: accounts = [] } = useQuery<Account[]>({
    queryKey: ["/api/crm/accounts", {
      ...(clientGroupId ? { clientGroupId } : {}),
    }],
    // ISSUE-128 Rework #1: Enable for BOTH new AND edit mode
    enabled: isOpen && ((applicantToAdd?.type === "account") || (editingParty?.partyType === "account") || (!isEdit && partyType === "account")),
  });

  // ISSUE-128 Rework #1: Fetch contacts for party selector (uses default queryFn with auth)
  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts", {
      ...(clientGroupId ? { clientGroupId } : {}),
    }],
    // ISSUE-128 Rework #1: Enable for BOTH new AND edit mode
    enabled: isOpen && ((applicantToAdd?.type === "contact") || (editingParty?.partyType === "contact") || (!isEdit && partyType === "contact")),
  });

  // ISSUE-128: Fetch existing facility parties (uses default queryFn with auth)
  const { data: existingParties = [], refetch: refetchParties } = useQuery<FacilityParty[]>({
    queryKey: ["/api/crm/facilities", facility?.id, "parties"],
    enabled: isOpen && isEdit && !!facility?.id,
  });

  // Filter parties by search term (used by Edit mode applicant selector)
  const _filteredAccounts = accounts.filter((account) =>
    account.legalName?.toLowerCase().includes(partySearchTerm.toLowerCase())
  );

  const _filteredContacts = contacts.filter((contact) => {
    const fullName = `${contact.firstName || ""} ${contact.lastName || ""}`.trim();
    return fullName.toLowerCase().includes(partySearchTerm.toLowerCase());
  });

  // Watch for status changes to auto-populate settled amount
  const facilityStatus = watch("facilityStatus");
  const approvedLimit = watch("approvedLimit");
  const _fundedAmount = watch("fundedAmount");

  // ISSUE-155: Clear product selection when lender changes
  useEffect(() => {
    if (watchLenderId !== previousLenderId) {
      // Only clear product if lender actually changed (not initial load)
      if (previousLenderId !== undefined) {
        setValue("lenderProductId", undefined);
      }
      setPreviousLenderId(watchLenderId);
    }
  }, [watchLenderId, previousLenderId, setValue]);

  const watchFacilityType = watch("facilityType");

  // F014/EDG-10101: Derive the facility name preview from lender + variant label + facility type.
  const derivedFacilityName = useMemo(() => {
    const selectedLender = lenders.find((l) => l.id === watchLenderId);
    const lenderName = selectedLender?.lenderName ?? "(No lender)";
    const selectedFacilityTypeObj = FACILITY_TYPES.find((t) => t.value === watchFacilityType);
    const typeLabel = selectedFacilityTypeObj?.label ?? "Facility";
    return `${lenderName} — Facility A — ${typeLabel}`;
  }, [watchLenderId, watchFacilityType, lenders]);

  // F014: Keep facilityName form value in sync with derived name (Create mode)
  useEffect(() => {
    if (!isEdit) {
      setValue("facilityName", derivedFacilityName);
    }
  }, [derivedFacilityName, isEdit, setValue]);

  // F009: Watch interest rate fields for real-time totalRate calculation
  const watchBaseRate = watch("baseRate");
  const watchMargin = watch("margin");

  // F009: Calculate total rate in real-time (user enters as percentage, displays as percentage)
  const calculatedTotalRate = useMemo(() => {
    const base = parseFloat(watchBaseRate || "0");
    const marg = parseFloat(watchMargin || "0");
    if (isNaN(base) && isNaN(marg)) return "";
    const total = (isNaN(base) ? 0 : base) + (isNaN(marg) ? 0 : marg);
    return total.toFixed(2);
  }, [watchBaseRate, watchMargin]);

  // F009: Watch term fields for cross-field validation (ioMonths <= termMonths)
  const watchTermMonths = watch("termMonths");
  const watchIoMonths = watch("ioMonths");

  // F009: Trigger cross-field validation when term or IO months change
  useEffect(() => {
    // Only trigger if both fields have values (cross-field validation context)
    if (watchTermMonths || watchIoMonths) {
      // Trigger validation on ioMonths field to re-run the refine check
      trigger("ioMonths");
    }
  }, [watchTermMonths, watchIoMonths, trigger]);

  // ISSUE-119: Trigger confirmation dialog when status changes to "settled"
  useEffect(() => {
    // Only trigger dialog if:
    // 1. Status changed TO "settled"
    // 2. Previous status was NOT "settled" (avoid re-triggering when editing existing settled facilities)
    // 3. Approved limit exists (to pre-fill)
    if (
      facilityStatus === "settled" &&
      previousFacilityStatus !== undefined &&
      previousFacilityStatus !== "settled" &&
      approvedLimit
    ) {
      // Pre-fill the dialog input with approved limit
      setSettledAmountInput(approvedLimit);
      setShowSettledConfirm(true);
    }
    // Track previous status for change detection
    if (facilityStatus !== previousFacilityStatus) {
      setPreviousFacilityStatus(facilityStatus);
    }
  }, [facilityStatus, approvedLimit, previousFacilityStatus]);

  // Reset form with facility data when editing (fix for edit modal pre-population)
  useEffect(() => {
    if (isOpen && facility) {
      reset({
        facilityName: facility.facilityName,
        facilityType: facility.facilityType || "",
        facilityStatus: facility.facilityStatus || "indicative",
        requestedAmount: facility.requestedAmount || "",
        approvedLimit: facility.approvedLimit || "",
        fundedAmount: facility.fundedAmount || "",
        lenderId: facility.lenderId || "",
        lenderProductId: facility.lenderProductId || "", // ISSUE-155: Include lenderProductId
        applicationDate: facility.applicationDate || "",
        approvalDate: facility.approvalDate || "",
        settlementDate: facility.settlementDate || "",
        maturityDate: facility.maturityDate || "",
        // F009: Interest & Repayment fields - convert decimal to percentage for display
        baseRate: decimalToPercent(facility.baseRate) || "",
        margin: decimalToPercent(facility.margin) || "",
        termMonths: facility.termMonths?.toString() || "",
        ioMonths: facility.ioMonths?.toString() || "",
        amortisationPeriodMonths: facility.amortisationPeriodMonths?.toString() || "", // F011
        repaymentFrequency: facility.repaymentFrequency || "",
        repaymentOverride: facility.repaymentOverride || "",
        balloonAmount: facility.balloonAmount || "",
        balloonPct: decimalToPercent((facility as Facility & { balloonPct?: string | null }).balloonPct) || "",
        customCommissionRate: decimalToPercent(facility.customCommissionRate) || "",
        loanType: facility.loanType || "",
      });
      // Reset party state when editing (not needed for edit)
      setSelectedPartyId("");
      setPartySearchTerm("");
      setPartyError(null);
    } else if (isOpen && !facility) {
      reset({
        facilityName: "",
        facilityType: "term_loan", // F009: Default facility type to prevent validation blocking
        facilityStatus: "indicative",
        requestedAmount: "",
        approvedLimit: "",
        fundedAmount: "",
        lenderId: "",
        lenderProductId: "", // ISSUE-155: Include lenderProductId
        applicationDate: "",
        approvalDate: "",
        settlementDate: "",
        maturityDate: "",
        // F009: Interest & Repayment fields
        baseRate: "",
        margin: "",
        termMonths: "",
        ioMonths: "",
        amortisationPeriodMonths: "", // F011
        repaymentFrequency: "",
        repaymentOverride: "",
        balloonAmount: "",
        balloonPct: "",
        customCommissionRate: "",
        loanType: "",
      });
      // Reset party state for new facility - F009: Use defaults from deal if available
      setPartyType(defaultBorrowerType || "account");
      setSelectedPartyId(defaultBorrowerId || "");
      setPartySearchTerm("");
      setPartyError(null);
      setIsAdditionalDetailsOpen(false);
      // ISSUE-128: Reset facility parties for new facility
      setFacilityParties([]);
      // ISSUE-155: Reset product selector state
      setShowProductModal(false);
      setPreviousLenderId(undefined);
      // F015: Reset parties for Create mode
      setCreateParties([]);
      setPartyValidationError(null);
      // F016: Reset pricing schedules
      setPricingSchedules(createDefaultSchedules());
      // F017: Reset fees and dates
      setCreateFees([]);
      setFinanceClauseDate("");
      setExpectedSettlementDate("");
    }
  }, [isOpen, facility, reset]);

  // ISSUE-128: Load existing facility parties when editing
  useEffect(() => {
    if (isOpen && isEdit && existingParties.length > 0) {
      setFacilityParties(existingParties);
    }
  }, [isOpen, isEdit, existingParties]);

  const createMutation = useMutation({
    mutationFn: async (data: FacilityFormData & {
      parties: CreatePartyEntry[];
      pricingSchedules: PricingScheduleFormData[];
      fees: FeeFormData[];
      financeClauseDate?: string;
      expectedSettlementDate?: string;
    }) => {
      // F011: Create facility with nested children
      const facilityResponse = await apiRequest("POST", "/api/crm/facilities", {
        ...data,
        dealId,
        // F017: Include dates
        financeClauseDate: data.financeClauseDate || undefined,
        expectedSettlementDate: data.expectedSettlementDate || undefined,
        // F011: Nested children arrays
        pricingSchedules: buildFacilityCreatePricingSchedules(data.pricingSchedules),
        fees: data.fees,
        parties: buildFacilityCreateParties(data.parties),
      });

      const facilityData = await facilityResponse.json();
      return facilityData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/facilities"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/deals", dealId, "facilities"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals"] });
      // ISSUE-021 Rework #1: Invalidate specific deal to trigger auto-refresh of Financials tab
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals", dealId] });
      toast({
        title: "Facility created",
        description: "The facility has been created with the borrower party.",
      });
      reset();
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create facility",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: FacilityFormData) => {
      return await apiRequest("PATCH", `/api/crm/facilities/${facility!.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/facilities"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/facilities", facility!.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/deals", dealId, "facilities"],
      });
      // ISSUE-021 Rework #1: Invalidate specific deal to trigger auto-refresh of Financials tab
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals", dealId] });
      toast({
        title: "Facility updated",
        description: "The facility has been updated successfully.",
      });
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update facility",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FacilityFormData) => {
    // F015: Validate parties — at least 1 borrower/co_borrower required
    if (!isEdit) {
      const hasBorrower = createParties.some(
        (p) => p.partyRole === "borrower" || p.partyRole === "co_borrower"
      );
      if (!hasBorrower) {
        setPartyValidationError("At least one borrower or co-borrower is required.");
        return;
      }
      setPartyValidationError(null);
    }

    // Validate settled amount when status is "settled"
    if (data.facilityStatus === "settled" && !data.fundedAmount) {
      toast({
        title: "Validation Error",
        description: "Settled Amount is required when facility status is 'settled'.",
        variant: "destructive",
      });
      return;
    }

    // Transform empty strings to undefined for date fields to prevent PostgreSQL validation errors
    // F009: Convert percentage fields to decimal for API (user enters 4.35, API expects 0.0435)
    // F014: For new facilities, use the auto-derived name
    const resolvedFacilityName = !isEdit ? derivedFacilityName : data.facilityName.trim();
    const sanitizedData: Record<string, unknown> = {
      ...data,
      facilityName: resolvedFacilityName || data.facilityName.trim(),
      facilityType: data.facilityType?.trim() || "standard",
      facilityStatus: data.facilityStatus?.trim() || "indicative",
      applicationDate: data.applicationDate?.trim() || undefined,
      approvalDate: data.approvalDate?.trim() || undefined,
      settlementDate: data.settlementDate?.trim() || undefined,
      maturityDate: data.maturityDate?.trim() || undefined,
      lenderId: data.lenderId?.trim() || undefined,
      lenderProductId: data.lenderProductId?.trim() || undefined, // ISSUE-155: Include lenderProductId
      // F009: Convert percentage to decimal for rate fields
      baseRate: data.baseRate?.trim() ? percentToDecimal(data.baseRate) : undefined,
      margin: data.margin?.trim() ? percentToDecimal(data.margin) : undefined,
      customCommissionRate: data.customCommissionRate?.trim() ? percentToDecimal(data.customCommissionRate) : undefined,
      // F009: Convert string to number for integer fields
      termMonths: data.termMonths?.trim() ? parseInt(data.termMonths) : undefined,
      ioMonths: data.ioMonths?.trim() ? parseInt(data.ioMonths) : undefined,
      amortisationPeriodMonths: data.amortisationPeriodMonths?.trim() ? parseInt(data.amortisationPeriodMonths) : undefined, // F011
      // F009: Pass through other fields
      repaymentFrequency: data.repaymentFrequency?.trim() || undefined,
      repaymentOverride: data.repaymentOverride?.trim() || undefined,
      balloonAmount: data.balloonAmount?.trim() !== "" ? data.balloonAmount : undefined,
      balloonPct: data.balloonPct?.trim() ? percentToDecimal(data.balloonPct) : undefined,
      loanType: data.loanType?.trim() || undefined,
    };

    if (isEdit) {
      updateMutation.mutate(sanitizedData as FacilityFormData);
    } else {
      createMutation.mutate({
        ...sanitizedData,
        parties: createParties,
        pricingSchedules,
        fees: createFees,
        financeClauseDate: financeClauseDate || undefined,
        expectedSettlementDate: expectedSettlementDate || undefined,
      } as FacilityFormData & {
        parties: CreatePartyEntry[];
        pricingSchedules: PricingScheduleFormData[];
        fees: FeeFormData[];
        financeClauseDate?: string;
        expectedSettlementDate?: string;
      });
    }
  };

  const handleClose = () => {
    reset();
    setSelectedPartyId("");
    setPartySearchTerm("");
    setPartyError(null);
    setIsAdditionalDetailsOpen(false);
    setShowDeleteConfirm(false);
    // ISSUE-119: Reset settled confirmation dialog state
    setShowSettledConfirm(false);
    setSettledAmountInput("");
    setPreviousFacilityStatus(undefined);
    // F015: Reset parties
    setCreateParties([]);
    setPartyValidationError(null);
    // F016: Reset pricing schedules
    setPricingSchedules(createDefaultSchedules());
    // F017: Reset fees and dates
    setCreateFees([]);
    setFinanceClauseDate("");
    setExpectedSettlementDate("");
    onClose();
  };

  const handleDeleteConfirm = () => {
    if (facility?.id && onDelete) {
      onDelete(facility.id);
      setShowDeleteConfirm(false);
      handleClose();
    }
  };

  // ISSUE-119: Handler for settled confirmation dialog Cancel
  const handleSettledCancel = () => {
    // Revert status back to previous value
    if (previousFacilityStatus !== undefined && previousFacilityStatus !== "settled") {
      setValue("facilityStatus", previousFacilityStatus);
    }
    setShowSettledConfirm(false);
    setSettledAmountInput("");
  };

  // ISSUE-119: Handler for settled confirmation dialog Confirm
  const handleSettledConfirm = () => {
    // Validate amount > 0
    const amount = parseFloat(settledAmountInput);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Settled Amount must be greater than zero.",
        variant: "destructive",
      });
      return;
    }

    // Set the funded amount from dialog input
    setValue("fundedAmount", settledAmountInput);
    setShowSettledConfirm(false);
    setSettledAmountInput("");

    toast({
      title: "Settled Amount Confirmed",
      description: `Settled Amount set to $${parseFloat(settledAmountInput).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Facility" : "Add Facility"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update facility details and manage applicants (borrowers, co-borrowers, guarantors)."
              : "Create a new facility by entering details and selecting the primary borrower."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* ============================================ */}
          {/* MAIN SECTION - Core Required Fields */}
          {/* ============================================ */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Facility Name */}
              <div className="col-span-2">
                <Label htmlFor="facilityName">
                  Facility Name {isEdit && <span className="text-destructive">*</span>}
                </Label>
                {isEdit ? (
                  <>
                    <Input
                      id="facilityName"
                      {...register("facilityName")}
                      placeholder="e.g., Facility A - Equipment Finance"
                      className="mt-1"
                    />
                    {errors.facilityName && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.facilityName.message}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    {/* F014: Auto-derived name preview (read-only) */}
                    <Input
                      id="facilityName"
                      data-testid="facility-name-preview"
                      value={derivedFacilityName}
                      readOnly
                      disabled
                      className="mt-1 bg-muted text-muted-foreground"
                      placeholder="(No lender) — Facility A — Facility"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Name is auto-derived from lender and facility type.
                    </p>
                  </>
                )}
              </div>

              {/* Facility Type - ISSUE-118: Now required */}
              <div>
                <Label htmlFor="facilityType">
                  Facility Type <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={watch("facilityType") || ""}
                  onValueChange={(value) => setValue("facilityType", value)}
                >
                  <SelectTrigger className="mt-1" data-testid="select-facility-type">
                    <SelectValue placeholder="Select facility type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FACILITY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Use Facility Type for the finance product class; capture the asset as Vehicle or Equipment in the security record.
                </p>
                {errors.facilityType && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.facilityType.message}
                  </p>
                )}
              </div>

              {/* Facility Status — F014: Only shown in Edit mode (locked to indicative at Create) */}
              {isEdit && (
                <div>
                  <Label htmlFor="facilityStatus">Status</Label>
                  <Select
                    value={watch("facilityStatus") || "indicative"}
                    onValueChange={(value) => setValue("facilityStatus", value)}
                  >
                    <SelectTrigger className="mt-1" data-testid="select-facility-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="indicative">Indicative</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="settled">Settled</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                      <SelectItem value="withdrawn">Withdrawn</SelectItem>
                      <SelectItem value="scenario_not_proceeding">Scenario Not Proceeding</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Lender - ISSUE-118: Conditional requirement */}
              <div className={
                ["submitted", "approved", "settled"].includes(facilityStatus || "")
                  ? "border-2 border-primary rounded-lg p-3 -m-1"
                  : ""
              }>
                <Label htmlFor="lenderId" className={
                  ["submitted", "approved", "settled"].includes(facilityStatus || "")
                    ? "font-semibold"
                    : ""
                }>
                  Lender {["submitted", "approved", "settled"].includes(facilityStatus || "") && (
                    <span className="text-destructive">*</span>
                  )}
                </Label>
                {["submitted", "approved", "settled"].includes(facilityStatus || "") && (
                  <p className="text-xs text-muted-foreground mt-1 mb-2">
                    Required when facility is submitted, approved, or settled.
                  </p>
                )}
                <Select
                  value={watch("lenderId") || "__none__"}
                  onValueChange={(value) => setValue("lenderId", value === "__none__" ? undefined : value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={isLoadingLenders ? "Loading..." : "Select lender"} />
                  </SelectTrigger>
                  <SelectContent>
                    {!["submitted", "approved", "settled"].includes(facilityStatus || "") && (
                      <SelectItem value="__none__">No lender</SelectItem>
                    )}
                    {lenders.map((lender) => (
                      <SelectItem key={lender.id} value={lender.id}>
                        {lender.lenderName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.lenderId && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.lenderId.message}
                  </p>
                )}
              </div>

              {/* ISSUE-155: Cascading Product Selector (appears when lender is selected) */}
              {watchLenderId && watchLenderId !== "__none__" && (
                <div>
                  <Label htmlFor="lenderProductId">Lender Product</Label>
                  <div className="flex gap-2 mt-1">
                    <Select
                      value={watch("lenderProductId") || "none"}
                      onValueChange={(value) => setValue("lenderProductId", value === "none" ? undefined : value)}
                    >
                      <SelectTrigger className="flex-1" data-testid="select-lender-product">
                        <SelectValue placeholder={isLoadingProducts ? "Loading products..." : "Select product (optional)"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select product (optional)</SelectItem>
                        {lenderProducts.map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.productName} {product.productCode ? `(${product.productCode})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowProductModal(true)}
                      title="Add new product for this lender"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {lenderProducts.length === 0 && !isLoadingProducts && (
                    <p className="text-xs text-muted-foreground mt-1">
                      No products available for this lender. Click the + button to add one.
                    </p>
                  )}
                </div>
              )}

              {/* Requested Amount - F011: CurrencyInput */}
              <div>
                <Label htmlFor="requestedAmount">Requested Amount</Label>
                <CurrencyInput
                  id="requestedAmount"
                  value={watch("requestedAmount") || ""}
                  onValueChange={(value) => setValue("requestedAmount", value)}
                  placeholder="Enter amount"
                  className="mt-1"
                  data-testid="requestedAmount"
                />
              </div>
            </div>

            {/* ============================================ */}
            {/* F015: PARTIES SECTION (New facilities only) */}
            {/* ============================================ */}
            {!isEdit && (
              <CreatePartiesSection
                parties={createParties}
                onChange={(parties) => {
                  setCreateParties(parties);
                  // Clear validation error when parties change
                  if (parties.some((p) => p.partyRole === "borrower" || p.partyRole === "co_borrower")) {
                    setPartyValidationError(null);
                  }
                }}
                clientGroupId={clientGroupId}
                error={partyValidationError}
              />
            )}

            {/* ISSUE-127: Approved Limit & Settled Amount - Show only in Edit mode */}
            {isEdit && (
              <div className="grid grid-cols-2 gap-4">
                {/* Approved Limit - ISSUE-118: Conditional requirement */}
                <div className={
                  ["approved", "settled"].includes(facilityStatus || "")
                    ? "border-2 border-primary rounded-lg p-3 -m-1"
                    : ""
                }>
                  <Label htmlFor="approvedLimit" className={
                    ["approved", "settled"].includes(facilityStatus || "")
                      ? "font-semibold"
                      : ""
                  }>
                    Approved Limit {["approved", "settled"].includes(facilityStatus || "") && (
                      <span className="text-destructive">*</span>
                    )}
                  </Label>
                  {["approved", "settled"].includes(facilityStatus || "") && (
                    <p className="text-xs text-muted-foreground mt-1 mb-2">
                      Required when facility is approved or settled.
                    </p>
                  )}
                  <Input
                    id="approvedLimit"
                    {...register("approvedLimit")}
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className="mt-1"
                  />
                  {errors.approvedLimit && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.approvedLimit.message}
                    </p>
                  )}
                </div>
                {/* Settled Amount - ISSUE-119: Conditional requirement */}
                <div className={facilityStatus === "settled" ? "border-2 border-primary rounded-lg p-3 -m-1" : ""}>
                  <Label htmlFor="fundedAmount" className={facilityStatus === "settled" ? "font-semibold" : ""}>
                    Settled Amount {facilityStatus === "settled" && <span className="text-destructive">*</span>}
                  </Label>
                  {facilityStatus === "settled" && (
                    <p className="text-xs text-muted-foreground mt-1 mb-2">
                      Pre-filled with Approved Limit. Adjust if needed.
                    </p>
                  )}
                  <Input
                    id="fundedAmount"
                    {...register("fundedAmount")}
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className="mt-1"
                  />
                  {errors.fundedAmount && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.fundedAmount.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ISSUE-118: Conditional Date Fields - Show when required */}
            {/* Approval Date - Required when approved/settled */}
            {["approved", "settled"].includes(facilityStatus || "") && (
              <div className="border-2 border-primary rounded-lg p-3">
                <Label htmlFor="approvalDate" className="font-semibold">
                  Approval Date <span className="text-destructive">*</span>
                </Label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">
                  Required when facility is approved or settled.
                </p>
                <Input
                  id="approvalDate"
                  {...register("approvalDate")}
                  type="date"
                  className="mt-1"
                />
                {errors.approvalDate && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.approvalDate.message}
                  </p>
                )}
              </div>
            )}

            {/* Settlement Date - Required when settled */}
            {facilityStatus === "settled" && (
              <div className="border-2 border-primary rounded-lg p-3">
                <Label htmlFor="settlementDate" className="font-semibold">
                  Settlement Date <span className="text-destructive">*</span>
                </Label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">
                  Required when facility is settled.
                </p>
                <Input
                  id="settlementDate"
                  {...register("settlementDate")}
                  type="date"
                  className="mt-1"
                />
                {errors.settlementDate && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.settlementDate.message}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ============================================ */}
          {/* ISSUE-128: APPLICANTS SECTION - Edit mode only */}
          {/* ============================================ */}
          {isEdit && (
            <div className="space-y-3 border rounded-lg p-4 bg-muted/10">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Applicants</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Manage borrowers, co-borrowers, and guarantors for this facility.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setApplicantToAdd({
                      type: "account",
                      id: "",
                      role: "borrower",
                    });
                  }}
                >
                  <Plus className="mr-2 h-3 w-3" />
                  Add Applicant
                </Button>
              </div>

              {/* Add/Edit Applicant Form - ISSUE-128 Rework #1: Support both add AND edit */}
              {(applicantToAdd || editingParty) && (
                <div className="border rounded-md p-3 bg-background space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">{editingParty ? "Edit Applicant" : "New Applicant"}</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => {
                        setApplicantToAdd(null);
                        setEditingParty(null);
                        setApplicantSearchTerm("");
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    {/* Type Selector - ISSUE-128 Rework #1: Pre-populate for edit */}
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select
                        value={editingParty ? editingParty.partyType : applicantToAdd!.type}
                        onValueChange={(value: "account" | "contact") => {
                          if (editingParty) {
                            // For edit mode: can't change type, only role
                            return;
                          } else {
                            setApplicantToAdd({ ...applicantToAdd!, type: value, id: "" });
                            setApplicantSearchTerm("");
                          }
                        }}
                        disabled={!!editingParty} // Disable type change when editing
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="account">Account</SelectItem>
                          <SelectItem value="contact">Contact</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Name Selector - ISSUE-128 Rework #1: Pre-populate for edit, client group filter */}
                    <div>
                      <Label className="text-xs">Name</Label>
                      <Select
                        value={editingParty ? (editingParty.partyType === "account" ? editingParty.accountId : editingParty.contactId) || "" : applicantToAdd!.id}
                        onValueChange={(value) => {
                          if (editingParty) {
                            // For edit mode: can't change party, only role
                            return;
                          } else {
                            setApplicantToAdd({ ...applicantToAdd!, id: value });
                          }
                        }}
                        disabled={!!editingParty} // Disable party change when editing
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder={`Select ${editingParty ? editingParty.partyType : applicantToAdd!.type}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {(editingParty ? editingParty.partyType : applicantToAdd!.type) === "account"
                            ? (clientGroupId ? accounts.filter(a => a.clientGroupId === clientGroupId) : accounts).map((account) => (
                                <SelectItem key={account.id} value={account.id}>
                                  {account.legalName}
                                </SelectItem>
                              ))
                            : (clientGroupId ? contacts.filter(c => c.clientGroupId === clientGroupId) : contacts).map((contact) => (
                                <SelectItem key={contact.id} value={contact.id}>
                                  {contact.firstName} {contact.lastName}
                                </SelectItem>
                              ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Role Selector - ISSUE-128 Rework #1: Pre-populate for edit, allow role changes */}
                    <div>
                      <Label className="text-xs">Role</Label>
                      <Select
                        value={editingParty ? editingParty.partyRole : applicantToAdd!.role}
                        onValueChange={(value) => {
                          if (editingParty) {
                            setEditingParty({ ...editingParty, partyRole: value });
                          } else {
                            setApplicantToAdd({ ...applicantToAdd!, role: value });
                          }
                        }}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FACILITY_PARTY_ROLES.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    onClick={async () => {
                      // ISSUE-128 Rework #1: Handle both add AND edit modes
                      if (editingParty) {
                        // EDIT MODE: Update party role via PATCH
                        try {
                          await apiRequest("PATCH", `/api/crm/facilities/${facility?.id}/parties/${editingParty.id}`, {
                            partyRole: editingParty.partyRole,
                          });

                          // Refetch parties
                          await refetchParties();

                          toast({
                            title: "Applicant Updated",
                            description: "The applicant role has been updated.",
                          });

                          setEditingParty(null);
                          setApplicantSearchTerm("");
                        } catch (error: any) {
                          toast({
                            title: "Error",
                            description: error.message || "Failed to update applicant",
                            variant: "destructive",
                          });
                        }
                      } else if (applicantToAdd) {
                        // ADD MODE: Create new party via POST
                        if (!applicantToAdd.id || !facility?.id) return;

                        try {
                          // Add party via API
                          const partyPayload: any = {
                            facilityId: facility.id,
                            partyType: applicantToAdd.type,
                            partyRole: applicantToAdd.role,
                          };
                          if (applicantToAdd.type === "account") {
                            partyPayload.accountId = applicantToAdd.id;
                          } else {
                            partyPayload.contactId = applicantToAdd.id;
                          }

                          await apiRequest("POST", `/api/crm/facilities/${facility.id}/parties`, partyPayload);

                          // Refetch parties
                          await refetchParties();

                          toast({
                            title: "Applicant Added",
                            description: "The applicant has been added to this facility.",
                          });

                          setApplicantToAdd(null);
                          setApplicantSearchTerm("");
                        } catch (error: any) {
                          toast({
                            title: "Error",
                            description: error.message || "Failed to add applicant",
                            variant: "destructive",
                          });
                        }
                      }
                    }}
                    disabled={editingParty ? false : (!applicantToAdd || !applicantToAdd.id)}
                  >
                    {editingParty ? "Update Applicant" : "Add to Facility"}
                  </Button>
                </div>
              )}

              {/* Applicants Table */}
              {facilityParties.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No applicants added yet. Click "Add Applicant" to add one.
                </p>
              ) : (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow className="h-8 bg-muted/50">
                        <TableHead className="text-xs font-medium px-2 py-0.5 w-[100px]">Type</TableHead>
                        <TableHead className="text-xs font-medium px-2 py-0.5">Name</TableHead>
                        <TableHead className="text-xs font-medium px-2 py-0.5 w-[140px]">Role</TableHead>
                        <TableHead className="text-xs font-medium px-2 py-0.5 w-[60px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {facilityParties.map((party) => (
                        <TableRow key={party.id} className="h-10">
                          <TableCell className="px-2 py-0.5 text-xs">
                            {party.partyType === "account" ? "Account" : "Contact"}
                          </TableCell>
                          <TableCell className="px-2 py-0.5 text-xs">
                            {party.partyName || "Unknown"}
                          </TableCell>
                          <TableCell className="px-2 py-0.5 text-xs capitalize">
                            {party.partyRole.replace(/_/g, " ")}
                          </TableCell>
                          <TableCell className="px-2 py-0.5">
                            {/* ISSUE-128 Rework #1: Add Edit button */}
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => {
                                  // Pre-populate edit form with party data
                                  setEditingParty(party);
                                  setApplicantToAdd(null); // Close add form if open
                                }}
                                title="Edit applicant role"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={async () => {
                                // Validation: Keep at least 1 borrower
                                const borrowerCount = facilityParties.filter(
                                  (p) => p.partyRole === "borrower"
                                ).length;
                                if (party.partyRole === "borrower" && borrowerCount <= 1) {
                                  toast({
                                    title: "Cannot Remove",
                                    description: "At least one borrower must remain on the facility.",
                                    variant: "destructive",
                                  });
                                  return;
                                }

                                try {
                                  // Delete party via API
                                  await apiRequest("DELETE", `/api/crm/facilities/${facility?.id}/parties/${party.id}`, {});

                                  // Refetch parties
                                  await refetchParties();

                                  toast({
                                    title: "Applicant Removed",
                                    description: "The applicant has been removed from this facility.",
                                  });
                                } catch (error: any) {
                                  toast({
                                    title: "Error",
                                    description: error.message || "Failed to remove applicant",
                                    variant: "destructive",
                                  });
                                }
                              }}
                              title="Delete applicant"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* ============================================ */}
          {/* F009: INTEREST & REPAYMENT - Collapsible Section */}
          {/* ============================================ */}
          {isEdit && (
          <Collapsible
            open={isInterestRepaymentOpen}
            onOpenChange={setIsInterestRepaymentOpen}
          >
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                className="flex w-full justify-between p-2 hover:bg-muted/50"
              >
                <span className="text-sm font-medium">Interest & Repayment</span>
                {isInterestRepaymentOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              <div className="grid grid-cols-2 gap-4 border rounded-lg p-4 bg-muted/20">
                {/* Loan Type */}
                <div>
                  <Label htmlFor="loanType">Loan Type</Label>
                  <Select
                    value={watch("loanType") || "none"}
                    onValueChange={(value) => setValue("loanType", value === "none" ? undefined : value)}
                  >
                    <SelectTrigger className="mt-1" data-testid="loanType">
                      <SelectValue placeholder="Select loan type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select loan type</SelectItem>
                      {LOAN_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Base Rate (%) */}
                <div>
                  <Label htmlFor="baseRate">Base Rate (%)</Label>
                  <Input
                    id="baseRate"
                    {...register("baseRate")}
                    type="number"
                    step="any"
                    placeholder="e.g., 4.35"
                    className="mt-1"
                    data-testid="baseRate"
                  />
                  {errors.baseRate && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.baseRate.message}
                    </p>
                  )}
                </div>

                {/* Margin (%) */}
                <div>
                  <Label htmlFor="margin">Margin (%)</Label>
                  <Input
                    id="margin"
                    {...register("margin")}
                    type="number"
                    step="any"
                    placeholder="e.g., 1.50"
                    className="mt-1"
                    data-testid="margin"
                  />
                  {errors.margin && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.margin.message}
                    </p>
                  )}
                </div>

                {/* Total Rate (calculated, read-only) */}
                <div>
                  <Label htmlFor="totalRate">Total Rate (%)</Label>
                  <Input
                    id="totalRate"
                    value={calculatedTotalRate ? `${calculatedTotalRate}%` : ""}
                    placeholder="Auto-calculated"
                    className="mt-1 bg-muted"
                    disabled
                    data-testid="totalRate"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Base Rate + Margin
                  </p>
                </div>

                {/* Term Months */}
                <div>
                  <Label htmlFor="termMonths">Term (months)</Label>
                  <Input
                    id="termMonths"
                    {...register("termMonths")}
                    type="number"
                    step="1"
                    min="0"
                    placeholder="e.g., 60"
                    className="mt-1"
                    data-testid="termMonths"
                  />
                </div>

                {/* I/O Months */}
                <div>
                  <Label htmlFor="ioMonths">I/O Period (months)</Label>
                  <Input
                    id="ioMonths"
                    {...register("ioMonths")}
                    type="number"
                    step="1"
                    min="0"
                    placeholder="e.g., 12"
                    className="mt-1"
                    data-testid="ioMonths"
                  />
                  {errors.ioMonths && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.ioMonths.message}
                    </p>
                  )}
                </div>

                {/* F011: Amortisation Period (commercial loans only) */}
                {["commercial_loan", "equipment_finance", "business_loan", "property_development"].includes(watch("loanType") || "") && (
                  <div>
                    <Label htmlFor="amortisationPeriodMonths">Amortisation Period (months)</Label>
                    <Input
                      id="amortisationPeriodMonths"
                      {...register("amortisationPeriodMonths")}
                      type="number"
                      step="1"
                      min="0"
                      placeholder="e.g., 300"
                      className="mt-1"
                      data-testid="amortisationPeriodMonths"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      For P&I calculation. Leave empty to use term months.
                    </p>
                    {errors.amortisationPeriodMonths && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.amortisationPeriodMonths.message}
                      </p>
                    )}
                  </div>
                )}

                {/* Repayment Frequency */}
                <div>
                  <Label htmlFor="repaymentFrequency">Repayment Frequency</Label>
                  <Select
                    value={watch("repaymentFrequency") || "none"}
                    onValueChange={(value) => setValue("repaymentFrequency", value === "none" ? undefined : value)}
                  >
                    <SelectTrigger className="mt-1" data-testid="repaymentFrequency">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select frequency</SelectItem>
                      {REPAYMENT_FREQUENCIES.map((freq) => (
                        <SelectItem key={freq.value} value={freq.value}>
                          {freq.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Repayment Override */}
                <div>
                  <Label htmlFor="repaymentOverride">Repayment Override ($)</Label>
                  <Input
                    id="repaymentOverride"
                    {...register("repaymentOverride")}
                    type="number"
                    step="0.01"
                    placeholder="Override calculated repayment"
                    className="mt-1"
                    data-testid="repaymentOverride"
                  />
                </div>

                {/* Balloon / Residual Amount */}
                <div>
                  <Label htmlFor="balloonAmount">Balloon / Residual Amount ($)</Label>
                  <Input
                    id="balloonAmount"
                    {...register("balloonAmount")}
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="e.g., 25000"
                    className="mt-1"
                    data-testid="balloonAmount"
                  />
                </div>

                {/* Balloon / Residual Percentage */}
                <div>
                  <Label htmlFor="balloonPct">Balloon / Residual (%)</Label>
                  <Input
                    id="balloonPct"
                    {...register("balloonPct")}
                    type="number"
                    step="any"
                    min="0"
                    max="100"
                    placeholder="e.g., 30"
                    className="mt-1"
                    data-testid="balloonPct"
                  />
                </div>

                {/* I/O Repayment (read-only from DB) - Always visible per E2E tests */}
                <div>
                  <Label>I/O Repayment (calculated)</Label>
                  <Input
                    value={facility?.ioRepayment
                      ? `$${parseFloat(facility.ioRepayment).toLocaleString('en-AU', { minimumFractionDigits: 2 })}`
                      : "Calculated after save"}
                    className="mt-1 bg-muted"
                    disabled
                    data-testid="ioRepayment"
                  />
                </div>

                {/* P&I Repayment (read-only from DB trigger) - Always visible per E2E tests */}
                <div>
                  <Label>P&I Repayment (calculated)</Label>
                  <Input
                    value={facility?.piRepayment
                      ? `$${parseFloat(facility.piRepayment).toLocaleString('en-AU', { minimumFractionDigits: 2 })}`
                      : "Calculated after save"}
                    className="mt-1 bg-muted"
                    disabled
                    data-testid="piRepayment"
                  />
                </div>

                {/* Custom Commission Rate (%) — F014: Edit mode only (not shown at Create) */}
                {isEdit && (
                  <div className="col-span-2 pt-2 border-t mt-2">
                    <Label htmlFor="customCommissionRate">Custom Commission Rate (%)</Label>
                    <p className="text-xs text-muted-foreground mb-1">
                      Overrides the lender's default commission rate for this facility.
                    </p>
                    <Input
                      id="customCommissionRate"
                      {...register("customCommissionRate")}
                      type="number"
                      step="0.01"
                      placeholder="e.g., 1.25"
                      className="mt-1 max-w-xs"
                      data-testid="customCommissionRate"
                    />
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
          )}

          {/* ============================================ */}
          {/* F016: PRICING SCHEDULE SECTION (Create mode only) */}
          {/* ============================================ */}
          {!isEdit && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Pricing Schedule</Label>
              <PricingScheduleSection
                schedules={pricingSchedules}
                onChange={setPricingSchedules}
                mode="form"
              />
            </div>
          )}

          {/* ============================================ */}
          {/* F017: TERM & REPAYMENT (Create mode — computed from pricing schedule) */}
          {/* ============================================ */}
          {!isEdit && (
            <TermRepaymentSection schedules={pricingSchedules} />
          )}

          {/* ============================================ */}
          {/* F017: FEES SECTION (Create mode) */}
          {/* ============================================ */}
          {!isEdit && (
            <FeesSection fees={createFees} onChange={setCreateFees} />
          )}

          {/* ============================================ */}
          {/* F017: DATES (Create mode — finance clause + expected settlement) */}
          {/* ============================================ */}
          {!isEdit && (
            <div data-testid="create-dates-section" className="space-y-3">
              <Label className="text-sm font-medium">Key Dates</Label>
              <div className="grid grid-cols-2 gap-4 border rounded-lg p-4 bg-muted/20">
                <div>
                  <Label htmlFor="financeClauseDate" className="text-xs">Finance Clause Date</Label>
                  <Input
                    id="financeClauseDate"
                    data-testid="input-finance-clause-date"
                    type="date"
                    value={financeClauseDate}
                    onChange={(e) => setFinanceClauseDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="expectedSettlementDate" className="text-xs">Expected Settlement Date</Label>
                  <Input
                    id="expectedSettlementDate"
                    data-testid="input-expected-settlement-date"
                    type="date"
                    value={expectedSettlementDate}
                    onChange={(e) => setExpectedSettlementDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ============================================ */}
          {/* ADDITIONAL DETAILS - Collapsible Section (Edit mode only — F014) */}
          {/* ============================================ */}
          {isEdit && <Collapsible
            open={isAdditionalDetailsOpen}
            onOpenChange={setIsAdditionalDetailsOpen}
          >
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                className="flex w-full justify-between p-2 hover:bg-muted/50"
              >
                <span className="text-sm font-medium">Additional Details</span>
                {isAdditionalDetailsOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              <div className="grid grid-cols-2 gap-4 border rounded-lg p-4 bg-muted/20">
                {/* Application Date */}
                <div>
                  <Label htmlFor="applicationDate">Application Date</Label>
                  <Input
                    id="applicationDate"
                    {...register("applicationDate")}
                    type="date"
                    className="mt-1"
                  />
                </div>

                {/* Approval Date - Only show here if NOT required (i.e., not approved/settled) */}
                {!["approved", "settled"].includes(facilityStatus || "") && (
                  <div>
                    <Label htmlFor="approvalDate">Approval Date</Label>
                    <Input
                      id="approvalDate"
                      {...register("approvalDate")}
                      type="date"
                      className="mt-1"
                    />
                  </div>
                )}

                {/* Settlement Date - Only show here if NOT required (i.e., not settled) */}
                {facilityStatus !== "settled" && (
                  <div>
                    <Label htmlFor="settlementDate">Settlement Date</Label>
                    <Input
                      id="settlementDate"
                      {...register("settlementDate")}
                      type="date"
                      className="mt-1"
                    />
                  </div>
                )}

                {/* Maturity Date */}
                <div>
                  <Label htmlFor="maturityDate">Maturity Date</Label>
                  <Input
                    id="maturityDate"
                    {...register("maturityDate")}
                    type="date"
                    className="mt-1"
                  />
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>}

          {/* ============================================ */}
          {/* DANGER ZONE - Edit mode only */}
          {/* ============================================ */}
          {isEdit && onDelete && (
            <Collapsible
              open={isDangerZoneOpen}
              onOpenChange={setIsDangerZoneOpen}
            >
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="flex w-full justify-between p-2 hover:bg-destructive/5 text-destructive"
                >
                  <span className="text-sm font-medium">Danger Zone</span>
                  {isDangerZoneOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <div className="border border-destructive/50 rounded-lg p-4 bg-destructive/5">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-destructive">Delete this facility</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Once deleted, this facility and all associated data will be permanently removed. This action cannot be undone.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="ml-4"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? "Saving..."
                : isEdit
                ? "Update Facility"
                : "Add Facility"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Facility</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{facility?.facilityName}"? This action cannot be undone and will permanently remove this facility and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Facility
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ISSUE-119: Settled Confirmation Dialog */}
      <AlertDialog open={showSettledConfirm} onOpenChange={(open) => {
        if (!open) handleSettledCancel();
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Settled Amount</AlertDialogTitle>
            <AlertDialogDescription>
              The facility status is being changed to "Settled". Please confirm the Settled Amount.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="settledAmountInput">
              Settled Amount <span className="text-destructive">*</span>
            </Label>
            <Input
              id="settledAmountInput"
              type="number"
              step="0.01"
              value={settledAmountInput}
              onChange={(e) => setSettledAmountInput(e.target.value)}
              placeholder="0.00"
              className="mt-2"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Pre-filled with Approved Limit. Edit if needed.
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleSettledCancel}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleSettledConfirm}>
              Confirm Amount
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ISSUE-155: Product Form Modal (quick-add) */}
      <ProductFormModal
        open={showProductModal}
        onOpenChange={setShowProductModal}
        defaultLenderId={watchLenderId}
        onProductCreated={async (newProduct) => {
          // ISSUE-129 Pattern: Await query invalidation before auto-selecting
          await queryClient.invalidateQueries({
            queryKey: ["/api/crm/lenders", watchLenderId, "products"],
          });
          // Auto-select the newly created product
          setValue("lenderProductId", newProduct.id);
          setShowProductModal(false);
          toast({
            title: "Product created",
            description: `${newProduct.productName} has been added and selected.`,
          });
        }}
      />
    </Dialog>
  );
}
